import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
    AppBar,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    Grid as MuiGrid,
    IconButton,
    Stack,
    TextField,
    Toolbar,
    Tooltip,
    Typography
} from "@mui/material";
import { Clock as ClockIcon } from "react-feather";
import { useTheme } from "@mui/styles";
import { Close, Login, Logout } from "@mui/icons-material";
import styled from "styled-components/macro";
import { spacing } from "@mui/system";

import Clock from "react-clock";
import "react-clock/dist/Clock.css";
import TimeService from "../../services/TimeService";
import { useSnackbar } from "notistack";
import errorHandler from "../../utils/errors";
import _ from "lodash";
import moment from "moment-timezone";
const Grid = styled(MuiGrid)(spacing);

const getEntryTime = (entry) => moment(entry.date || entry.createdAt).valueOf();
const isClockPunch = (entry) => ["IN", "OUT"].includes(entry.type);

const getTimeClockState = (entries, now) => {
    if (_.isEmpty(entries)) {
        return {
            clockedIn: false,
            missedOut: false
        };
    }

    const currentTime = moment(now);
    const sortedEntries = _.orderBy(entries, [getEntryTime], ["asc"]);
    const latestClockPunch = [...sortedEntries].reverse().find(isClockPunch);

    if (!latestClockPunch) {
        return {
            clockedIn: false,
            missedOut: false
        };
    }

    const clockedIn = latestClockPunch.type === "IN";

    if (clockedIn) {
        const lastChance = moment(latestClockPunch.date || latestClockPunch.createdAt).add(12, "hours");

        if (currentTime.isAfter(lastChance)) {
            return {
                clockedIn: false,
                missedOut: true
            };
        }
    }

    return {
        clockedIn,
        missedOut: false
    };
};

const TimeClock = () => {

    const theme = useTheme();
    const user = useSelector((state) => state.auth.user);
    const { enqueueSnackbar } = useSnackbar();

    const [open, setOpen] = useState(false);
    const [clockedIn, setClockedIn] = useState(false);
    const [missedOut, setMissedOut] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [pendingPunchType, setPendingPunchType] = useState(null);
    const [timeEntries, setTimeEntries] = useState([]);
    const [timeValue, setTimeValue] = useState(new Date());
    const [noteValue, setNoteValue] = useState("");
    const submittingRef = useRef(false);

    const fetchTimeEntries = useCallback(async ({ showError = true } = {}) => {
        if (!user?.id) return;

        const times = await TimeService.find({ user: user.id }).catch(err => {
            if (showError) {
                errorHandler(err, enqueueSnackbar);
            }

            return null;
        });

        if (times?.data) {
            setTimeEntries(times.data);
        }
    }, [enqueueSnackbar, user?.id]);

    /**
     * Open Time Clock
     */
    const handleTimeClockOpen = () => {
        setOpen(true);
        fetchTimeEntries({ showError: false });
    };

    /**
     * Close Time Clock
     */
    const handleTimeClockClose = () => {
        setOpen(false);
    };

    /**
     * Start time clock
     * Get user times and get last entry to see if clocked in/out
     * @type {(function(): Promise<void>)|*}
     */
    useEffect(() => {
        const interval = setInterval(() => setTimeValue(new Date()), 1000);

        return () => {
            clearInterval(interval);
        };
    }, []);

    useEffect(() => {
        let active = true;

        (async () => {
            if (active) {
                await fetchTimeEntries();
            }
        })();

        return () => {
            active = false;
        };
    }, [fetchTimeEntries]);

    useEffect(() => {
        if (!open && !missedOut) return undefined;

        const interval = setInterval(() => {
            fetchTimeEntries({ showError: false });
        }, 15000);

        return () => {
            clearInterval(interval);
        };
    }, [fetchTimeEntries, missedOut, open]);

    useEffect(() => {
        const handleFocus = () => {
            fetchTimeEntries({ showError: false });
        };

        window.addEventListener("focus", handleFocus);

        return () => {
            window.removeEventListener("focus", handleFocus);
        };
    }, [fetchTimeEntries]);

    useEffect(() => {
        const nextState = getTimeClockState(timeEntries, timeValue);

        setClockedIn(nextState.clockedIn);
        setMissedOut(nextState.missedOut);
    }, [timeEntries, timeValue]);

    // Set up clock options
    const timeOptions = {
        hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit", timeZoneName: "short"
    };
    const formattedTime = timeValue.toLocaleTimeString("en-US", timeOptions);

    const dateOptions = { weekday: "long", month: "long", day: "numeric", year: "numeric" };
    const formattedDate = timeValue.toLocaleDateString("en-US", dateOptions);

    /**
     * Set Note Value Handler
     * @param e
     */
    const noteChange = (e) => {
        setNoteValue(e.target.value);
    };

    /**
     * Handle Clock In/Out
     * @return {Promise<void>}
     */
    const handleSubmit = async () => {
        if (submittingRef.current) return;

        const punchType = clockedIn ? "OUT" : "IN";

        submittingRef.current = true;
        setSubmitting(true);
        setPendingPunchType(punchType);

        try {
            const data = { user: user.id, date: new Date(), type: punchType, notes: noteValue };
            const times = await TimeService.create(data).catch(err => errorHandler(err, enqueueSnackbar));

            if (times && times.data) {
                setTimeEntries((current) => [...current, times.data]);
                setClockedIn(punchType === "IN");
                setMissedOut(false);
                setNoteValue('');
            }

            setTimeValue(new Date());
        } finally {
            submittingRef.current = false;
            setSubmitting(false);
            setPendingPunchType(null);
        }
    };

    const buttonLabel = submitting
        ? `Clocking ${pendingPunchType === "OUT" ? "Out" : "In"}...`
        : `${clockedIn ? "Clock Out" : "Clock In"}`;

    return (
        <>
            <Tooltip title={`Clocked ${clockedIn ? "In" : "Out"}`}>
                <IconButton
                    aria-label="Time Clock"
                    aria-haspopup="true"
                    onClick={handleTimeClockOpen}
                    size="large"
                    sx={{ color: clockedIn ? theme.palette.primary.main : "inherit" }}
                >
                    <ClockIcon />
                </IconButton>
            </Tooltip>
            <Dialog
                open={open}
                onClose={handleTimeClockClose}
                fullWidth
                maxWidth="sm"
                sx={{
                    "& .MuiDialog-container > .MuiPaper-root": {
                        background: theme.palette.background.default
                    }
                }}>
                <AppBar sx={{ position: "relative", backgroundColor: theme.sidebar.background }}>
                    <Toolbar sx={{ mx: 6 }}>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h4" component="div">
                            Employee Time Clock
                        </Typography>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={handleTimeClockClose}
                            aria-label="close"
                        >
                            <Close />
                        </IconButton>
                    </Toolbar>
                </AppBar>
                <Grid container spacing={4} p={10} sx={{ display: "flex", height: "100%" }}>
                    <Grid item xs={12}>
                        <Card sx={{ height: "100%" }}>
                            <CardContent sx={{
                                height: "100%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center"
                            }}>
                                <Stack spacing={6} sx={{ display: "flex", width: "100%", alignItems: "center" }}>
                                    <Clock value={timeValue} size={200} />
                                    <Stack spacing={2}>
                                        <Typography variant="h1" sx={{ fontSize: 34, textAlign: "center" }}>
                                            {formattedTime}
                                        </Typography>
                                        <Typography variant="h5" sx={{ textAlign: "center" }}>
                                            {formattedDate}
                                        </Typography>
                                    </Stack>
                                    {missedOut ? (
                                        <Typography variant="h4" sx={{ textAlign: "center", color: theme.palette.error.main }}>
                                            You forgot to clock out! Please contact management to correct and re-enable time clock.
                                        </Typography>
                                    ) : (
                                        <Stack spacing={2} sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                                            <Button
                                                disabled={submitting}
                                                variant="contained"
                                                color={clockedIn ? "error" : "primary"}
                                                size="large"
                                                sx={{ width: "100%", maxWidth: 300, height: 60, fontSize: 22 }}
                                                onClick={handleSubmit}
                                                startIcon={submitting
                                                    ? <CircularProgress color="inherit" size={22} />
                                                    : clockedIn ? <Logout/> : <Login/>
                                                }
                                            >
                                                {buttonLabel}
                                            </Button>
                                            <TextField
                                                disabled={submitting}
                                                fullWidth
                                                multiline
                                                rows={3}
                                                id="noteInput"
                                                variant="outlined"
                                                value={noteValue}
                                                onChange={noteChange}
                                                placeholder="Punch notes..."
                                                sx={{ width: "100%", maxWidth: 300 }}
                                            />
                                        </Stack>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Dialog>
        </>
    );
};

export default TimeClock;
